import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { formatDate } from "@ckgo2/shared/utils";
import type { Communication } from "@ckgo2/shared/types";
import {
  Mail,
  Phone,
  MessageSquare,
  FileText,
  CreditCard,
  Clock,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Send,
  ArrowDownLeft,
  ArrowUpRight,
  Loader2,
} from "lucide-react";

interface CommunicationTimelineProps {
  orderId: string;
  customerId: string;
  communications: Communication[];
}

const TYPE_CONFIG: Record<
  string,
  { icon: typeof Mail; label: string; color: string }
> = {
  email: {
    icon: Mail,
    label: "Email",
    color: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
  },
  phone: {
    icon: Phone,
    label: "Telefon",
    color: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
  },
  note: {
    icon: MessageSquare,
    label: "Poznámka",
    color: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  },
  sms: {
    icon: MessageSquare,
    label: "SMS",
    color: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
  },
  status_change: {
    icon: RefreshCw,
    label: "Změna statusu",
    color: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
  },
  payment: {
    icon: CreditCard,
    label: "Platba",
    color: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
  },
  document: {
    icon: FileText,
    label: "Dokument",
    color: "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400",
  },
};

export default function CommunicationTimeline({
  orderId,
  customerId,
  communications,
}: CommunicationTimelineProps) {
  const queryClient = useQueryClient();
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteType, setNoteType] = useState<"note" | "phone" | "email">("note");
  const [noteSubject, setNoteSubject] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const addNoteMutation = useMutation({
    mutationFn: async (data: {
      type: string;
      subject: string;
      content: string;
      direction: string;
    }) => {
      return api.post(`/api/admin/orders/${orderId}/communications`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      setIsAddingNote(false);
      setNoteSubject("");
      setNoteContent("");
    },
  });

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const handleAddNote = () => {
    if (!noteContent.trim()) return;

    addNoteMutation.mutate({
      type: noteType,
      subject: noteSubject || (noteType === "note" ? "Interní poznámka" : noteSubject),
      content: noteContent,
      direction: noteType === "email" ? "outbound" : "internal",
    });
  };

  return (
    <div className="space-y-4">
      {/* Header with add button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Komunikace
        </h3>
        <button
          onClick={() => setIsAddingNote(!isAddingNote)}
          className="btn-ghost btn-sm"
        >
          <MessageSquare className="h-4 w-4" />
          Přidat záznam
        </button>
      </div>

      {/* Add note form */}
      {isAddingNote && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
          <div className="mb-4 flex gap-2">
            {(["note", "phone", "email"] as const).map((type) => {
              const config = TYPE_CONFIG[type];
              const Icon = config.icon;
              return (
                <button
                  key={type}
                  onClick={() => setNoteType(type)}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    noteType === type
                      ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                      : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {config.label}
                </button>
              );
            })}
          </div>

          <div className="space-y-3">
            {noteType !== "note" && (
              <input
                type="text"
                placeholder="Předmět..."
                value={noteSubject}
                onChange={(e) => setNoteSubject(e.target.value)}
                className="input"
              />
            )}
            <textarea
              placeholder={
                noteType === "note"
                  ? "Napište interní poznámku..."
                  : noteType === "phone"
                    ? "Shrnutí telefonátu..."
                    : "Obsah komunikace..."
              }
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              rows={3}
              className="input resize-none"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setIsAddingNote(false);
                  setNoteSubject("");
                  setNoteContent("");
                }}
                className="btn-ghost btn-sm"
              >
                Zrušit
              </button>
              <button
                onClick={handleAddNote}
                disabled={!noteContent.trim() || addNoteMutation.isPending}
                className="btn-primary btn-sm"
              >
                {addNoteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Uložit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      {communications.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-gray-500 dark:border-gray-600">
          <Clock className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          <p>Zatím žádná komunikace</p>
        </div>
      ) : (
        <div className="relative space-y-0">
          {/* Timeline line */}
          <div className="absolute left-5 top-0 h-full w-0.5 bg-gray-200 dark:bg-gray-700" />

          {communications.map((comm, index) => {
            const config = TYPE_CONFIG[comm.type] || TYPE_CONFIG.note;
            const Icon = config.icon;
            const isExpanded = expandedItems.has(comm.id);
            const hasLongContent = comm.content && comm.content.length > 150;

            return (
              <div key={comm.id} className="relative flex gap-4 pb-6">
                {/* Icon */}
                <div
                  className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${config.color}`}
                >
                  <Icon className="h-5 w-5" />
                </div>

                {/* Content */}
                <div className="flex-1 pt-1">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {comm.subject || config.label}
                        </span>
                        {comm.direction === "inbound" && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <ArrowDownLeft className="h-3 w-3" />
                            příchozí
                          </span>
                        )}
                        {comm.direction === "outbound" && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <ArrowUpRight className="h-3 w-3" />
                            odchozí
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {formatDate(comm.createdAt, { hour: "2-digit", minute: "2-digit" })}
                        {comm.createdBy && ` • ${comm.createdBy}`}
                      </p>
                    </div>
                  </div>

                  {comm.content && (
                    <div className="mt-2">
                      <p
                        className={`text-sm text-gray-600 dark:text-gray-400 ${
                          !isExpanded && hasLongContent ? "line-clamp-2" : ""
                        }`}
                      >
                        {comm.content}
                      </p>
                      {hasLongContent && (
                        <button
                          onClick={() => toggleExpand(comm.id)}
                          className="mt-1 flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp className="h-3 w-3" />
                              Méně
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-3 w-3" />
                              Více
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
